import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useGetProductList2 } from "../api/queries/productQueries";

export const useSearchProductPage = ({ type }: { type: string }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPageIndex = Number(searchParams.get("pageIndex")) || 0;
  const itemsPerPage = 12;

  // --- State for search filters & pagination ---
  const renderOrderingKey = {
    MUSICAL: `city${searchParams.get("city")}-cate41`,
    SIM: `city${searchParams.get("city")}-cate42`,
    TICKET: `city${searchParams.get("city")}-cate${searchParams.get("category") || 0}`,
    TOUR: `city${searchParams.get("city")}-cate${searchParams.get("category") || 0}`,
  };

  const [search, setSearch] = useState(() => {
    return {
      orderingKey: renderOrderingKey[type as keyof typeof renderOrderingKey],
      city: searchParams.get("city") || "",
      category: searchParams.get("category") || "",
      subCategory: searchParams.get("subCategory") || "",
      keyword: searchParams.get("keyword") || "",
      commonSort: searchParams.get("commonSort") || "",
      type,
      pageSize: itemsPerPage,
      pageIndex: initialPageIndex,
    };
  });

  useEffect(() => {
    setSearch((prev) => {
      return {
        ...prev,
        orderingKey: renderOrderingKey[type as keyof typeof renderOrderingKey],
        city: searchParams.get("city") || "",
        category: searchParams.get("category") || "",
        subCategory: searchParams.get("subCategory") || "",
        keyword: searchParams.get("keyword") || "",
        commonSort: searchParams.get("commonSort") || "",
      };
    });
  }, [searchParams]);

  const { data, isLoading } = useGetProductList2(search);

  const totalElements = data?.totalElements;

  const [dataProduct, setDataProduct] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (!data) return;

    const products = data.data || [];
    setDataProduct(products);
    setTotalPages(data.totalPages || 1);
  }, [data]);

  const handlePageChange = (newPage: number) => {
    setSearch((prev) => ({
      ...prev,
      pageIndex: newPage,
    }));
    // Update URL
    updateUrlParams({ pageIndex: newPage });
  };
  const updateUrlParams = (
    updates: Record<string, string | number | undefined>,
  ) => {
    const params = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === "" || value === undefined) {
        params.delete(key);
      } else {
        params.set(key, String(value));
      }
    });
    navigate({ search: params.toString() }, { replace: false });
  };

  // --- Category filter handler ---
  const onChooseCate = (cateId: number) => {
    const newCategory = cateId === 0 ? "" : cateId.toString();
    setSearch((prev) => ({
      ...prev,
      category: newCategory,
      pageIndex: 0,
    }));
    updateUrlParams({ category: newCategory, pageIndex: 0 });
  };

  // --- Sort change handler ---
  const onChooseCommonSort = (commonSort: string) => {
    const newSort = commonSort === search.commonSort ? "" : commonSort;
    setSearch((prev) => ({
      ...prev,
      commonSort: newSort,
      pageIndex: 0,
    }));
    updateUrlParams({ commonSort: newSort, pageIndex: 0 });
  };

  return {
    type,
    dataProduct,
    totalPages,
    currentPage: search.pageIndex,
    isLoading,
    search,
    totalElements,
    setSearch,
    handlePageChange,
    onChooseCate,
    onChooseCommonSort,
  };
};

export type TUseSearchProductPageResponse = ReturnType<
  typeof useSearchProductPage
>;
